import React, { useState, useEffect } from "react";
import SetupModal from "../components/modals/SetupModal";
import SettingsModal from "../components/modals/SettingsModal";
import TimerCard from "../components/cards/TimerCard";
import { useDogProfile } from "../hooks/useDogProfile";
import { useTimers } from "../hooks/useTimers";
import { apiService } from "../services/apiService";

const MainPage: React.FC = () => {
  const [showSetup, setShowSetup] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  const { dogProfile, loading, createDogProfile } = useDogProfile();
  const { timers, completeTimer } = useTimers(dogProfile);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize default activities
        await apiService.initializeDefaultActivities();
        
        // If no dog profile, show setup
        if (!loading && !dogProfile) {
          setShowSetup(true);
        }
      } catch (error) {
        console.error("Error initializing app:", error);
        if (!loading && !dogProfile) {
          setShowSetup(true);
        }
      }
    };

    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }

    initializeApp();
  }, [loading, dogProfile]);

  const handleSetupSubmit = async (dogName: string, dogImage: string) => {
    try {
      await createDogProfile(dogName, dogImage);
    } catch (error) {
      console.error("Error creating dog profile:", error);
    }
  };

  const handleTimerComplete = () => {
    // Timer refresh is handled automatically by the useTimers hook
  };

  const handleRefresh = async () => {
    // Refresh functionality would be handled by the hooks
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-br from-teal-400 to-teal-600 rounded-full p-6 inline-block mb-4 animate-bounce">
            <span className="text-4xl text-white">🐾</span>
          </div>
          <div className="text-gray-600 font-medium">Loading SharedDog...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 to-orange-200">
      <SetupModal
        isOpen={showSetup}
        onClose={() => setShowSetup(false)}
        onSubmit={handleSetupSubmit}
      />

      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        onRefresh={handleRefresh}
        dogProfile={dogProfile}
      />

      {dogProfile && (
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="relative inline-block">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 p-2 inline-block mb-4">
                <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                  {dogProfile.image_base64 ? (
                    <img 
                      src={dogProfile.image_base64} 
                      alt={dogProfile.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl">🐕</span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="absolute top-0 right-0 bg-white rounded-full p-2 shadow-lg hover:shadow-xl transition-shadow"
              >
                ⚙️
              </button>
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              {dogProfile.name}
            </h1>
            {dogProfile.breed && (
              <p className="text-gray-600">{dogProfile.breed}</p>
            )}
          </div>

          {/* Action Buttons Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-4 text-center shadow-lg">
              <div className="text-3xl mb-2">🚶</div>
              <div className="text-lg font-bold">Šetnja</div>
            </div>
            <div className="bg-white rounded-2xl p-4 text-center shadow-lg">
              <div className="text-3xl mb-2">🍽️</div>
              <div className="text-lg font-bold">Hranjenje</div>
            </div>
            <div className="bg-white rounded-2xl p-4 text-center shadow-lg">
              <div className="text-3xl mb-2">💩</div>
              <div className="text-lg font-bold">Br. 2</div>
            </div>
            <div className="bg-white rounded-2xl p-4 text-center shadow-lg">
              <div className="text-3xl mb-2">📝</div>
              <div className="text-lg font-bold">Beleške</div>
            </div>
          </div>

          {/* Today's Schedule */}
          <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Danas</h2>
            
            {timers.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🎉</div>
                <p className="text-gray-600">All caught up! No pending timers.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {timers
                  .sort((a, b) => a.minutesUntilDue - b.minutesUntilDue)
                  .map((timer, index) => (
                    <div key={`${timer.activityConfigId}-${index}`} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center space-x-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <div>
                          <div className="font-semibold">
                            {timer.name}
                          </div>
                          {timer.isOverdue && (
                            <div className="text-red-500 text-sm font-medium">Overdue</div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">
                          {timer.scheduleType === "specific_times" && timer.specificTimes.join(", ")}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Active Timer Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {timers
              .sort((a, b) => {
                // Sort by overdue first, then by time until due
                if (a.isOverdue && !b.isOverdue) return -1;
                if (!a.isOverdue && b.isOverdue) return 1;
                return a.minutesUntilDue - b.minutesUntilDue;
              })
              .map((timer, index) => (
                <TimerCard
                  key={`${timer.activityConfigId}-card-${index}`}
                  timer={timer}
                  onComplete={handleTimerComplete}
                  dogName={dogProfile.name}
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MainPage;